import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import { dataFilesListKey, fetchDataFiles } from '../features/dataFiles';
import { fetchTelemetrySessionContext } from '../features/telemetryContext';
import {
  getCachedCircuitMatch,
  getCachedCircuitMatchContexts,
  setCachedCircuitMatch,
} from '../lib/circuitDiscoveryCache';
import {
  dismissCircuitDiscoveryCandidate,
  getDismissedCircuitDiscoveries,
  listCircuitDiscoveryCandidates,
  listRecentUnassignedDiscoveryFiles,
  restoreCircuitDiscoveryCandidate,
  tryAssignMatchedCircuitForSession,
  type CircuitDiscoveryCandidate,
} from '../lib/circuitDiscoveries';
import { useTrackContributionCatalogSummary } from '../hooks/useTrackContributionCatalog';
import { useAuthStore } from '../store/authStore';

export type UseCircuitDiscoveriesResult = ReturnType<typeof useCircuitDiscoveries>;

export function useCircuitDiscoveries(enabled = true) {
  const teamId = useAuthStore((state) => state.user?.teamId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const [dismissedVersion, setDismissedVersion] = useState(0);
  const [contextVersion, setContextVersion] = useState(0);
  const [resolving, setResolving] = useState(false);
  const resolvedBatchKeyRef = useRef<string | null>(null);
  const [contextBySessionKey, setContextBySessionKey] = useState<
    Map<string, Awaited<ReturnType<typeof fetchTelemetrySessionContext>> | null>
  >(() => new Map());

  const { data: dataFilesPage, isLoading: dataFilesLoading } = useSWR(
    enabled ? dataFilesListKey({ source: 'ALL', perPage: 50 }) : null,
    () => fetchDataFiles({ source: 'ALL', perPage: 50 }),
  );
  const { contributions: mergedContributions, isLoading: contributionsLoading } =
    useTrackContributionCatalogSummary(enabled);
  const dataFiles = dataFilesPage?.data ?? [];
  const listLoading = enabled && (dataFilesLoading || contributionsLoading);

  const dismissedDiscoveries = useMemo(() => {
    void dismissedVersion;
    void userId;
    return getDismissedCircuitDiscoveries();
  }, [dismissedVersion, userId]);

  const recentFilesToResolve = useMemo(
    () => listRecentUnassignedDiscoveryFiles(dataFiles, mergedContributions, dismissedDiscoveries),
    [dataFiles, dismissedDiscoveries, mergedContributions],
  );
  const pendingCount = recentFilesToResolve.length;

  useEffect(() => {
    resolvedBatchKeyRef.current = null;
    setContextBySessionKey(teamId ? getCachedCircuitMatchContexts(teamId) : new Map());
    setContextVersion((value) => value + 1);
  }, [teamId]);

  useEffect(() => {
    if (!enabled) {
      setResolving(false);
      return;
    }
    if (listLoading) {
      setResolving(false);
      return;
    }

    const batchKey = recentFilesToResolve.map((file) => file.id).join('|');
    if (batchKey === resolvedBatchKeyRef.current) {
      return;
    }

    if (recentFilesToResolve.length === 0) {
      resolvedBatchKeyRef.current = batchKey;
      setResolving(false);
      return;
    }

    let cancelled = false;
    setResolving(true);

    void (async () => {
      const nextContext = new Map<string, Awaited<ReturnType<typeof fetchTelemetrySessionContext>> | null>();

      for (const file of recentFilesToResolve) {
        if (cancelled) {
          return;
        }

        let context = nextContext.get(file.id);
        if (context === undefined) {
          try {
            context = await fetchTelemetrySessionContext(file.id);
          } catch {
            context = null;
          }
          nextContext.set(file.id, context);
        }

        if (context?.contributionId) {
          continue;
        }

        const cachedMatch = getCachedCircuitMatch(file.id, file.updatedAt);
        if (cachedMatch?.status === 'unmatched') {
          continue;
        }

        if (cachedMatch?.status === 'matched' && cachedMatch.contributionId && cachedMatch.trackId) {
          nextContext.set(file.id, {
            sessionKey: file.id,
            teamId: context?.teamId ?? file.teamId,
            dataFileId: file.id,
            driverId: context?.driverId ?? file.driverId,
            kartId: context?.kartId ?? file.kartId,
            kartSetupHistoryEntryId: context?.kartSetupHistoryEntryId ?? null,
            trackId: cachedMatch.trackId,
            contributionId: cachedMatch.contributionId,
            recordedAtMs: context?.recordedAtMs ?? null,
            createdAt: context?.createdAt ?? file.createdAt,
            updatedAt: context?.updatedAt ?? file.updatedAt,
          });
          continue;
        }

        const match = await tryAssignMatchedCircuitForSession({
          dataFile: file,
          contributions: mergedContributions,
        });

        if (!match) {
          setCachedCircuitMatch({
            sessionKey: file.id,
            status: 'unmatched',
            contributionId: null,
            trackId: null,
            circuitName: null,
            fileUpdatedAt: file.updatedAt,
            resolvedAt: Date.now(),
          });
          continue;
        }

        setCachedCircuitMatch({
          sessionKey: file.id,
          status: 'matched',
          contributionId: match.draftId,
          trackId: match.trackId,
          circuitName: match.circuitName,
          fileUpdatedAt: file.updatedAt,
          resolvedAt: Date.now(),
        });

        nextContext.set(file.id, {
          sessionKey: file.id,
          teamId: context?.teamId ?? file.teamId,
          dataFileId: file.id,
          driverId: context?.driverId ?? file.driverId,
          kartId: context?.kartId ?? file.kartId,
          kartSetupHistoryEntryId: context?.kartSetupHistoryEntryId ?? null,
          trackId: match.trackId,
          contributionId: match.draftId,
          recordedAtMs: context?.recordedAtMs ?? null,
          createdAt: context?.createdAt ?? file.createdAt,
          updatedAt: context?.updatedAt ?? file.updatedAt,
        });
      }

      if (cancelled) {
        return;
      }

      resolvedBatchKeyRef.current = batchKey;
      setContextBySessionKey((current) => {
        const merged = new Map(current);
        for (const [sessionKey, context] of nextContext) {
          merged.set(sessionKey, context);
        }
        return merged;
      });
      setContextVersion((value) => value + 1);
      setResolving(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, listLoading, mergedContributions, recentFilesToResolve]);

  const candidates = useMemo(() => {
    void contextVersion;
    return listCircuitDiscoveryCandidates(dataFiles, mergedContributions, contextBySessionKey, dismissedDiscoveries);
  }, [contextBySessionKey, contextVersion, dataFiles, dismissedDiscoveries, mergedContributions]);

  const ready = enabled && !listLoading && !resolving;
  const hasNewTracks = ready && candidates.length > 0;

  const dismiss = useCallback((candidate: CircuitDiscoveryCandidate) => {
    dismissCircuitDiscoveryCandidate(candidate);
    resolvedBatchKeyRef.current = null;
    setDismissedVersion((value) => value + 1);
  }, []);

  const restore = useCallback((candidate: CircuitDiscoveryCandidate) => {
    restoreCircuitDiscoveryCandidate(candidate);
    resolvedBatchKeyRef.current = null;
    setDismissedVersion((value) => value + 1);
  }, []);

  const refreshContexts = useCallback(() => {
    resolvedBatchKeyRef.current = null;
    setContextBySessionKey(new Map());
    setContextVersion((value) => value + 1);
  }, []);

  return {
    candidates,
    pendingCount,
    hasNewTracks,
    ready,
    loading: listLoading,
    resolving,
    dismiss,
    restore,
    refreshContexts,
  };
}
